<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Check, ChevronRight, ClipboardCopy, Code2 } from 'lucide-vue-next';
import Button from '@/volt/Button.vue';
import ConsentConnectionGuide from './ConsentConnectionGuide.vue';
import { locale } from '@/i18n';
import { publishablePrivacyText } from '@/domain/legal-copy';

/**
 * Shared "Einbauen" step of every setup flow (first registration and the
 * site wizard): one tag, an optional cookie-banner connection and the
 * privacy text template.
 */
const props = defineProps<{
  snippet: string;
  policyVersion: string;
  businessForms?: boolean;
  privacyTemplates?: { version: string; de: string; en: string } | null;
}>();
const copied = ref<'' | 'tag' | 'privacy'>('');
const error = ref('');
const privacyLocale = ref<'de' | 'en'>(locale.value);
const privacyText = computed(() => publishablePrivacyText(props.privacyTemplates?.[privacyLocale.value] ?? ''));
watch(() => props.snippet, () => { copied.value = ''; error.value = ''; });
watch(privacyLocale, () => { if (copied.value === 'privacy') copied.value = ''; });
async function copy(kind: 'tag' | 'privacy', value: string) {
  try { await navigator.clipboard.writeText(value); copied.value = kind; error.value = ''; }
  catch { error.value = 'Kopieren nicht verfügbar. Bitte den Text markieren und manuell kopieren.'; }
}
</script>

<template>
  <section class="setup-step install-step" aria-labelledby="install-step-title">
    <header class="setup-step__intro">
      <h3 id="install-step-title">Ein Script für jede Website</h3>
      <p>Füge diesen Tag einmal in den Kopfbereich deiner Website ein. Kein Serverpaket, API-Schlüssel oder DNS-Eintrag nötig.</p>
    </header>
    <div class="install-code">
      <header><span><Code2 :size="16" aria-hidden="true" /> HTML-Tag</span><Button size="sm" variant="primary" @click="copy('tag', snippet)"><component :is="copied === 'tag' ? Check : ClipboardCopy" :size="14" /> {{ copied === 'tag' ? 'Kopiert' : 'Tag kopieren' }}</Button></header>
      <pre tabindex="0"><code>{{ snippet }}</code></pre>
    </div>
    <p v-if="error" class="setup-step__error" role="alert">{{ error }}</p>
    <ol class="setup-step__how">
      <li>Im Website-System das Feld für Code im Kopfbereich öffnen, oft „Head-Code“, „Custom Code“ oder „Header-Scripts“.</li>
      <li>Den Tag unverändert einfügen – einmal für alle Seiten.</li>
      <li>Speichern und veröffentlichen. Eine Vorschau im Editor reicht nicht.</li>
    </ol>
    <p class="setup-step__hint">Betreut eine Agentur deine Website? Schick ihr einfach diesen Tag.</p>

    <ConsentConnectionGuide :policy-version="policyVersion" :business-forms="businessForms" />

    <details v-if="privacyText" class="setup-disclosure privacy-disclosure">
      <summary>
        <span><strong>Datenschutztext übernehmen</strong><small>Vorlage für die Datenschutzerklärung deiner Website.</small></span>
        <ChevronRight :size="16" aria-hidden="true" />
      </summary>
      <div class="setup-disclosure__body">
        <div class="setup-disclosure__toolbar">
          <div class="install-guide__format" role="group" aria-label="Sprache des Datenschutztexts"><button type="button" :aria-pressed="privacyLocale === 'de'" @click="privacyLocale = 'de'">Deutsch</button><button type="button" :aria-pressed="privacyLocale === 'en'" @click="privacyLocale = 'en'">English</button></div>
          <Button size="sm" variant="ghost" @click="copy('privacy', privacyText)"><component :is="copied === 'privacy' ? Check : ClipboardCopy" :size="14" /> {{ copied === 'privacy' ? 'Kopiert' : 'Text kopieren' }}</Button>
        </div>
        <pre class="privacy-template-text" tabindex="0">{{ privacyText }}</pre>
        <p class="setup-step__muted">Vor dem Veröffentlichen prüfen und anpassen – die Vorlage ersetzt keine rechtliche Prüfung.</p>
      </div>
    </details>

    <p class="setup-step__legal">Moonshot ändert weder deine Website noch dein Cookie-Banner. Auch die Firmenerkennung ohne Cookie-Banner braucht eine Rechtsgrundlage (z. B. eine dokumentierte Interessenabwägung) und einen Hinweis in deiner Datenschutzerklärung.</p>
  </section>
</template>
